import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { gamificationService } from '@/services/gamification.service';

/*
 * A sequência de dias estudando (o "fogo"). Antes a tela lia `currentStreak`
 * de /gamification/points/me, que não traz esse campo: aparecia 0 para todo
 * mundo. E a API não acende o dia sozinha: só POST /streaks/update grava a
 * sequência, e ele recusa qualquer corpo (o UpdateStreakDto não tem validação
 * e o ValidationPipe barra campo não declarado). Sem mexer no backend, o dia
 * aceso vem do que a API já registra ao concluir uma aula pela primeira vez
 * (o ponto `lesson_completed` de /points/history), somado à sequência que o
 * banco guarda (/streaks/me). Dias seguidos até hoje ou ontem = a sequência.
 */

export interface Sequencia {
  currentStreak: number;
  longestStreak: number;
  lastActivityDate?: string | null;
}

const CHAVE = ['gamification', 'streak'];
const DIA = 86_400_000;

const diaLocal = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/** Meia-noite local de `n` dias antes (por calendário, não por 24 h). */
const diasAntes = (dia: number, n: number) => {
  const d = new Date(dia);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - n).getTime();
};

async function buscarSequencia(): Promise<Sequencia> {
  const [banco, pontos] = await Promise.all([
    gamificationService.getMyStreak().catch(() => null) as Promise<Sequencia | null>,
    gamificationService.getMyPointsLog().catch(() => []),
  ]);
  const acesos = new Set<number>();
  for (const p of pontos) {
    if (p.source === 'lesson_completed') acesos.add(diaLocal(new Date(p.createdAt)));
  }
  if (banco?.currentStreak && banco.lastActivityDate) {
    const fim = diaLocal(new Date(banco.lastActivityDate));
    for (let i = 0; i < banco.currentStreak; i++) acesos.add(diasAntes(fim, i));
  }
  const dias = [...acesos].sort((a, b) => a - b);
  if (!dias.length) {
    return { currentStreak: 0, longestStreak: banco?.longestStreak ?? 0, lastActivityDate: null };
  }
  let recorde = 0;
  let corrida = 0;
  dias.forEach((d, i) => {
    corrida = i > 0 && diasAntes(d, 1) === dias[i - 1] ? corrida + 1 : 1;
    recorde = Math.max(recorde, corrida);
  });
  const ultimo = dias[dias.length - 1];
  return {
    // `corrida` terminou no último dia aceso; sequenciaViva decide se ainda vale
    currentStreak: corrida,
    longestStreak: Math.max(recorde, banco?.longestStreak ?? 0),
    lastActivityDate: new Date(ultimo).toISOString(),
  };
}

/** Dias desde a última aula (0 = hoje, 1 = ontem; sem aula = Infinity). */
export function diasDesde(data?: string | null) {
  if (!data) return Infinity;
  return Math.round((diaLocal(new Date()) - diaLocal(new Date(data))) / DIA);
}

/** A sequência que ainda vale: estudou hoje ou ontem (o banco só zera de madrugada). */
export function sequenciaViva(s?: Sequencia | null) {
  if (!s?.currentStreak) return 0;
  return diasDesde(s.lastActivityDate) <= 1 ? s.currentStreak : 0;
}

export function useMinhaSequencia() {
  return useQuery<Sequencia>({
    queryKey: CHAVE,
    queryFn: buscarSequencia,
    staleTime: 60_000,
    retry: false,
  });
}

interface Comemoracao {
  aberta: boolean;
  /** de onde o número parte e onde chega */
  de: number;
  para: number;
  recorde: number;
  /** dia da última aula (acende os dias da semana até ele) */
  ultimoDia: string | null;
  /** só vendo o fogo (tocou na sequência), sem dia novo aceso */
  revendo: boolean;
  abrir: (c: Omit<Comemoracao, 'aberta' | 'abrir' | 'fechar'>) => void;
  fechar: () => void;
}

export const useComemoracao = create<Comemoracao>((set) => ({
  aberta: false,
  de: 0,
  para: 0,
  recorde: 0,
  ultimoDia: null,
  revendo: false,
  abrir: (c) => set({ ...c, aberta: true }),
  fechar: () => set({ aberta: false }),
}));

/** Mostra o fogo de quem já tem sequência (tocar no número da sequência). */
export function useVerSequencia() {
  const abrir = useComemoracao((s) => s.abrir);
  return useCallback(
    (s?: Sequencia | null) => {
      const dias = sequenciaViva(s);
      abrir({
        de: 0,
        para: dias,
        recorde: s?.longestStreak ?? dias,
        ultimoDia: dias ? (s?.lastActivityDate ?? null) : null,
        revendo: true,
      });
    },
    [abrir],
  );
}

/**
 * Depois de concluir uma aula: relê a sequência e, se o dia acendeu agora
 * (primeira aula do dia), abre a comemoração com o número subindo. O "antes"
 * é o da tela, carregado quando a aula abriu (por isso o hook já o busca).
 */
export function useRegistrarEstudo() {
  const qc = useQueryClient();
  const abrir = useComemoracao((s) => s.abrir);
  useMinhaSequencia();
  return useCallback(
    async () => {
      try {
        const antes = qc.getQueryData<Sequencia>(CHAVE);
        if (!antes) return;
        const depois = await buscarSequencia();
        qc.setQueryData(CHAVE, depois);
        const acendeuHoje =
          diasDesde(depois?.lastActivityDate) === 0 &&
          diasDesde(antes?.lastActivityDate) !== 0;
        if (acendeuHoje && depois.currentStreak > 0) {
          abrir({
            de: sequenciaViva(antes),
            para: depois.currentStreak,
            recorde: depois.longestStreak,
            ultimoDia: depois.lastActivityDate ?? null,
            revendo: false,
          });
        }
      } catch {
        // a sequência é um extra: se falhar, a aula continua concluída
      }
    },
    [qc, abrir],
  );
}
